<template>
  <div
    :key="currentElement && currentElement.id"
    class="sidebar sidebar-right"
    data-test="graphics-sidebar-right"
    @scroll="onScroll"
  >
    <multi-settings
      v-if="multipleSelected"
      :key="`multi-${settingsKey}`"
      :element="currentElement"
      :has-position-settings="hasPositionSettings"
      :has-animation-settings="showMultiAnimationSettings"
    />

    <template v-if="currentElement">
      <position-settings
        v-if="hasPositionSettings"
        :key="`position-${settingsKey}`"
        :element="currentElement"
        :are-same-type="areSelectedSameType"
        :are-same-parent="areSelectedSameParent"
      />
      <component
        :is="componentToUse"
        v-if="showElementSettingsPanel"
        :key="`component-${settingsKey}`"
        :element="currentElement"
        :scroll-offset="scrollTop"
        :template="template"
        @modal="$emit('modal', $event)"
        @open-google-sheets-modal="$emit('open-google-sheets-modal')"
      />
      <staff-settings
        v-if="isStaff"
        :key="`staff-${settingsKey}`"
        :element="currentElement"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import SettingsGroup from "./SettingsPanel/Group.vue";
import SettingsSlideshow from "./SettingsPanel/Slideshow.vue";
import SettingsText from "./SettingsPanel/Text.vue";
import SettingsTextSvg from "./SettingsPanel/TextSvg.vue";
import SettingsImage from "./SettingsPanel/Image.vue";
import SettingsVideo from "./SettingsPanel/Video.vue";
import SettingsMultiMedia from "./SettingsPanel/MultiMedia.vue";
import SettingsRectangle from "./SettingsPanel/Rectangle.vue";
import SettingsTriangle from "./SettingsPanel/Triangle.vue";
import SettingsOval from "./SettingsPanel/Oval.vue";
import SettingsDate from "./SettingsPanel/Date/Date.vue";
import SettingsQrcode from "./SettingsPanel/Qrcode.vue";
import PositionSettings from "./SettingsPanel/Position.vue";
import StaffSettings from "./SettingsPanel/Staff.vue";
import MultiSettings from "./SettingsPanel/MultiSettings.vue";
import SettingsTimer from "./SettingsPanel/Timer.vue";
import SettingsMarquee from "./SettingsPanel/Marquee/MarqueeSettings.vue";
import SettingsMarqueeText from "./SettingsPanel/Marquee/MarqueeTextSettings.vue";
import SettingsMarqueeImage from "./SettingsPanel/Marquee/MarqueeImageSettings.vue";

// IFrame
import SettingsFrame from "./SettingsPanel/Frame.vue";

// Scorestream
import SettingsScorestream from "./SettingsPanel/Scorestream.vue";

// Fox Foresight
import SettingsFoxForesight from './SettingsPanel/FoxForesight.vue';

// Sheet Data
import SettingsSheetData from "./SettingsPanel/Data/GoogleSheets/Settings.vue";
import SettingsDataList from "./SettingsPanel/DataList.vue";

// Social Data
import SettingsSocialData from "./SettingsPanel/Data/Social/Settings.vue";

// Social Display
import SettingsSocialDisplay from "./SettingsPanel/Data/SocialDisplay/Settings.vue";

// Poll Display
import SettingsPollDisplay from "./SettingsPanel/Data/PollDisplay/Settings.vue";

// Sportradar
import SettingsSportradar from "./SettingsPanel/Data/Sportradar/Settings.vue";

// Lists of elements that have specific settings
import {
  shapeTypes as shapes,
  elementTypesWithoutPosition,
  elementTypesWithoutAnimation,
} from "../utils/constants";

export default {
  emits: ['modal', 'open-google-sheets-modal'],
  name: "SidebarRight",

  components: {
    SettingsSlideshow,
    SettingsGroup,
    SettingsText,
    SettingsTextSvg,
    SettingsImage,
    SettingsVideo,
    SettingsRectangle,
    SettingsTriangle,
    SettingsOval,
    SettingsDate,
    SettingsQrcode,
    PositionSettings,
    StaffSettings,
    MultiSettings,
    SettingsTimer,
    SettingsSocialData,
    SettingsSocialDisplay,
    SettingsPollDisplay,
    SettingsFrame,
    SettingsScorestream,
    SettingsFoxForesight,
    SettingsSheetData,
    SettingsDataList,
    SettingsMultiMedia,
    SettingsSportradar,
    SettingsMarquee,
    SettingsMarqueeText,
    SettingsMarqueeImage,
    SettingsShoppable: SettingsGroup,
  },

  props: {
    template: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      scrollTop: 0,
    };
  },

  computed: {
    ...mapGetters("graphicsTemplate", [
      "getElementById",
      "getElementParentGroupId",
    ]),

    isStaff() {
      return this.$store.state.user.isStaff;
    },

    selectedElements() {
      return this.$store.state.templates.selectedElements;
    },

    multipleSelected() {
      return this.selectedElements.length > 1;
    },

    areSelectedSameType() {
      if (this.multipleSelected) {
        const typeToCheck = this.getElementById(this.selectedElements[0]).type;
        return this.selectedElements.every(
          (id) => this.getElementById(id).type === typeToCheck
        );
      }

      return false;
    },

    areSelectedAllShapes() {
      if (this.multipleSelected) {
        return this.selectedElements.every((id) =>
          shapes.includes(this.getElementById(id).type)
        );
      }

      return false;
    },

    areSelectedSameParent() {
      if (this.multipleSelected) {
        const toCheck = this.getElementParentGroupId(this.selectedElements[0]);
        return this.selectedElements.every(
          (id) => this.getElementParentGroupId(id) === toCheck
        );
      }

      return false;
    },

    hasPositionSettings() {
      if (this.multipleSelected) {
        return this.selectedElements.every((id) => {
          const element = this.getElementById(id);
          return elementTypesWithoutPosition.indexOf(element.type) === -1;
        });
      }

      return (
        this.currentElement &&
        elementTypesWithoutPosition.indexOf(this.currentElement.type) === -1
      );
    },

    hasAnimationSettings() {
      if (this.multipleSelected && !this.areSelectedSameType) {
        return this.selectedElements.every((id) => {
          const element = this.getElementById(id);
          return elementTypesWithoutAnimation.indexOf(element.type) === -1;
        });
      }

      return (
        this.currentElement &&
        elementTypesWithoutAnimation.indexOf(this.currentElement.type) === -1
      );
    },

    showComponentSettings() {
      if (this.multipleSelected) {
        const blockMultiEdit = ["slideshow"];

        return (
          (this.areSelectedSameType || this.areSelectedAllShapes) &&
          !blockMultiEdit.includes(this.currentElement.type)
        );
      }

      return true;
    },

    showElementSettingsPanel() {
      return !!this.currentElement && this.showComponentSettings && !!this.componentToUse;
    },

    /**
     * The element settings panel renders its own AnimationSettings whenever it
     * is shown. The multi-select panel must not render a second copy, otherwise
     * the duplicate vee-validate field names leave stale `required` errors on
     * the form after the panels unmount (e.g. unselecting a layer).
     */
    showMultiAnimationSettings() {
      return this.hasAnimationSettings && !this.showElementSettingsPanel;
    },

    currentElement() {
      if (!this.selectedElements.length) return null;

      if (
        this.multipleSelected &&
        (this.areSelectedSameType || this.areSelectedAllShapes)
      ) {
        // use the first element as reference if multiple elements are selected
        return this.getElementById(this.selectedElements[0]);
      }

      const [id] = this.selectedElements;

      return this.getElementById(id);
    },

    componentToUse() {
      if (this.areSelectedAllShapes && !this.areSelectedSameType) {
        // mix of shapes, triangle is most restrictive so check for that, otherwise use oval
        return this.selectedElements.some(
          (id) => this.getElementById(id).type === "triangle"
        )
          ? "settings-triangle"
          : "settings-oval";
      }

      // linked data container isn't really an element and doesn't have settings
      // so return null to prevent error about component not existing
      if (this.currentElement.type === "linked-data") {
        return null;
      }

      if (this.currentElement.type === "frame" && this.multipleSelected) {
        // Don't show frame settings if multiple elements are selected
        return null;
      }

      // Check if this is a Scorestream element (uses frame type but has isScorestream flag)
      if (
        this.currentElement.type === "frame" &&
        this.currentElement.isScorestream
      ) {
        return "settings-scorestream";
      }

      // Check if this is a Social Display element
      if (this.currentElement.type === "social-display") {
        return "settings-social-display";
      }

      // Check if this is a Poll Display element
      if (this.currentElement.type === "poll-display") {
        return "settings-poll-display";
      }

      // Check if this is a Fox Foresight element (uses image type but has isFoxForesight flag)
      if (this.currentElement.type === 'image' && this.currentElement.isFoxForesight) {
        return 'settings-fox-foresight';
      }

      return `settings-${this.currentElement.type}`;
    },

    settingsKey() {
      return this.selectedElements.reduce((acc, id) => `${acc}-${id}`, "");
    },
  },

  watch: {
    selectedElements() {
      this.scrollTop = 0;
    },
  },

  methods: {
    onScroll(e) {
      this.scrollTop = e.target.scrollTop;
    },
  },
};
</script>
